import { useMemo, useState } from 'react';
import { Library, Search, Play, ToggleLeft, ToggleRight } from 'lucide-react';
import { getModoIdentificar, setModoIdentificar } from '../../lib/modo-identificar';
import { CATALOGO_COMPONENTES, type TipoPeca } from './catalogo-componentes';
import { CATALOGO_DEMOS } from './catalogo-demos';
import { Modal } from '../../components/Modal';

// Tela Configurações → Catálogo de Componentes. Read-only: lista as peças
// compartilhadas do sistema (fonte em catalogo-componentes.ts). Existe pra não se
// perder entre os componentes conforme o sistema cresce. Ver convenção no .ts.

type Filtro = 'todos' | TipoPeca;

const TIPO_ROTULO: Record<TipoPeca, string> = {
  componente: 'Componente',
  comportamento: 'Comportamento',
};
// Cores de tipo daltonismo-safe (azul vs âmbar, nunca verde/vermelho).
const TIPO_COR: Record<TipoPeca, { bg: string; fg: string }> = {
  componente: { bg: '#dbeafe', fg: '#1e3a8a' },
  comportamento: { bg: '#fde68a', fg: '#000' },
};

function BadgeTipo({ tipo }: { tipo: TipoPeca }) {
  const c = TIPO_COR[tipo];
  return (
    <span style={{ backgroundColor: c.bg, color: c.fg, fontWeight: 600, fontSize: 11, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {TIPO_ROTULO[tipo]}
    </span>
  );
}

// Modal de demo ao vivo — renderiza o componente real com dados fixos.
function ModalDemo({ numero, nome, onFechar }: { numero: string; nome: string; onFechar: () => void }) {
  const renderDemo = CATALOGO_DEMOS[numero];
  if (!renderDemo) return null;
  return (
    <Modal aberto onFechar={onFechar} titulo={`Exemplo — ${nome}`} largura={560}>
      <div style={{ padding: '4px 0' }}>
        {renderDemo()}
      </div>
    </Modal>
  );
}

export function CatalogoComponentes() {
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busca, setBusca] = useState('');
  const [demoAberta, setDemoAberta] = useState<{ numero: string; nome: string } | null>(null);
  const [modoAtivo, setModoAtivo] = useState(() => getModoIdentificar());

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return CATALOGO_COMPONENTES.filter((p) => {
      if (filtro !== 'todos' && p.tipo !== filtro) return false;
      if (!termo) return true;
      return (
        p.nome.toLowerCase().includes(termo) ||
        p.oQueFaz.toLowerCase().includes(termo) ||
        p.ondeUsada.toLowerCase().includes(termo) ||
        p.numero.includes(termo)
      );
    });
  }, [filtro, busca]);

  const contagem = useMemo(() => ({
    todos: CATALOGO_COMPONENTES.length,
    componente: CATALOGO_COMPONENTES.filter((p) => p.tipo === 'componente').length,
    comportamento: CATALOGO_COMPONENTES.filter((p) => p.tipo === 'comportamento').length,
  }), []);

  const abas: { id: Filtro; rotulo: string; n: number }[] = [
    { id: 'todos', rotulo: 'Todos', n: contagem.todos },
    { id: 'componente', rotulo: 'Componentes', n: contagem.componente },
    { id: 'comportamento', rotulo: 'Comportamentos', n: contagem.comportamento },
  ];

  return (
    <div>
      <div className="pagina-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 className="pagina-titulo">
          <Library size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Catálogo de Componentes
        </h2>
      </div>

      <div style={{ fontSize: 14, color: 'var(--page-fg)', marginBottom: 14, maxWidth: 820 }}>
        As peças reutilizáveis do sistema — feitas uma vez e usadas em várias telas, pra não duplicar código:
        mexeu na peça, muda em todo mundo que usa. <b>Componente</b> é um pedaço de tela pronto;
        <b> comportamento</b> é uma regra reutilizada (sem desenhar nada). A lista cresce conforme construímos.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {abas.map((a) => {
          const ativa = filtro === a.id;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setFiltro(a.id)}
              style={{
                padding: '6px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: ativa ? '1px solid var(--accent)' : '1px solid var(--border)',
                background: ativa ? 'var(--accent)' : 'transparent',
                color: ativa ? '#fff' : 'var(--text-muted)',
              }}
            >
              {a.rotulo} ({a.n})
            </button>
          );
        })}
        <div
          role="button"
          onClick={() => { const novo = !modoAtivo; setModoIdentificar(novo); setModoAtivo(novo); }}
          title="Contorna e nomeia os componentes em todas as telas (admin)"
          style={{
            marginLeft: 'auto',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
            background: modoAtivo ? '#fde68a' : 'transparent',
            color: modoAtivo ? '#000' : 'var(--text-muted)',
            border: `1px solid ${modoAtivo ? '#f59e0b' : 'var(--border)'}`,
            transition: 'background 0.2s',
          }}
        >
          {modoAtivo
            ? <ToggleRight size={18} style={{ color: '#b45309' }} />
            : <ToggleLeft size={18} />}
          Modo identificar nas telas
        </div>
        <div style={{ position: 'relative', minWidth: 240 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar peça..."
            className="pl-8"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <table className="tabela" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 56, textAlign: 'center' }}>Nº</th>
            <th>Peça</th>
            <th style={{ width: 130 }}>Tipo</th>
            <th>O que faz</th>
            <th style={{ width: 260 }}>Onde aparece</th>
            <th style={{ width: 110 }}></th>
          </tr>
        </thead>
        <tbody>
          {lista.length === 0 ? (
            <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhuma peça encontrada.</td></tr>
          ) : (
            lista.map((p) => {
              const temDemo = p.tipo === 'componente' && p.numero in CATALOGO_DEMOS;
              return (
                <tr key={p.numero}>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--text-muted)' }}>{p.numero}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.nome}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{p.arquivo}</div>
                  </td>
                  <td><BadgeTipo tipo={p.tipo} /></td>
                  <td style={{ fontSize: 13 }}>{p.oQueFaz}</td>
                  <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{p.ondeUsada}</td>
                  <td style={{ textAlign: 'center' }}>
                    {temDemo && (
                      <button
                        type="button"
                        title="Ver exemplo ao vivo"
                        onClick={() => setDemoAberta({ numero: p.numero, nome: p.nome })}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          padding: '4px 10px', borderRadius: 6,
                          border: '1px solid var(--accent)',
                          background: 'transparent', color: 'var(--accent)',
                        }}
                      >
                        <Play size={12} />
                        Ver exemplo
                      </button>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {demoAberta && (
        <ModalDemo
          numero={demoAberta.numero}
          nome={demoAberta.nome}
          onFechar={() => setDemoAberta(null)}
        />
      )}
    </div>
  );
}
